const publicBase = import.meta.env.BASE_URL.endsWith('/')
  ? import.meta.env.BASE_URL
  : `${import.meta.env.BASE_URL}/`;
const skin = `${publicBase}skins/oro-mx-h5`;

const ORO_ASSETS_PREFIX = 'https://img.oroassets.cc/file/nc88-img/';
const LOCAL_COVER_PREFIX = '/skins/oro-mx-h5/covers/';

const normalizeOrigin = (origin: string | undefined) => (origin || '').trim().replace(/\/$/, '');

export const coverCdnOrigin = () => normalizeOrigin(import.meta.env.VITE_PUBLIC_COVER_ORIGIN);

/** 后台 iconUrl 是站点根路径 `/skins/...`，墨西哥包挂在 `/mx/` 下，必须拼上 BASE_URL。 */
export const resolveOroMxPublicUrl = (url: string | null | undefined, base = publicBase) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith(base)) return url;
  if (url.startsWith('/')) return `${base}${url.slice(1)}`;
  return `${base}${url}`;
};

/** 源站封面热链先改本机镜像；配置了公开源后再改绝对地址。 */
export const rewriteOroMxCoverUrl = (
  url: string | null | undefined,
  origin = coverCdnOrigin(),
) => {
  if (!url) return '';
  let next = url;
  if (url.startsWith(ORO_ASSETS_PREFIX)) {
    next = `${LOCAL_COVER_PREFIX}${url.slice(ORO_ASSETS_PREFIX.length).split('?')[0]}`;
  }
  void origin;
  return next;
};

export const resolveOroMxCoverUrl = (
  url: string | null | undefined,
  base = publicBase,
  origin = coverCdnOrigin(),
) => resolveOroMxPublicUrl(rewriteOroMxCoverUrl(url, origin), base);

export type OroMxTab = {
  icon: string;
  iconKind: 'svg' | 'img';
  id: string;
  label: string;
  viewBox?: string;
};

export type OroMxGame = {
  id: string;
  image: string;
  title: string;
  venueCode?: string;
};

export const oroMxAssetPaths = {
  avatar: `${skin}/brand/avatar.png`,
  authClose: `${skin}/auth/close.svg`,
  authSignupGift: `${skin}/auth/signup-gift.png`,
  authHideEye: `${skin}/auth/hide-eye.svg`,
  authUser: `${skin}/auth/user.svg`,
  firstDepositClose: `${skin}/first-deposit/close.png`,
  firstDepositRibbonLeft: `${skin}/first-deposit/ribbon-left.png`,
  firstDepositRibbonRight: `${skin}/first-deposit/ribbon-right.png`,
  firstDepositStar: `${skin}/first-deposit/star.png`,
  floatJackpot: `${skin}/float/jackpot-window.webp`,
  floatScratch: `${skin}/float/scratch.webp`,
  floatSignIn: `${skin}/float/sign-in.webp`,
  floatWheel: `${skin}/float/wheel.webp`,
  titanOne: `${skin}/fonts/TitanOne-Regular.ttf`,
  footer18: `${skin}/footer/18plus.webp`,
  footerFlagMx: `${skin}/footer/flag-mx.webp`,
  footerGamblers: `${skin}/footer/gamblers.png`,
  footerLangArrow: `${skin}/footer/lang-arrow.png`,
  footerPayments: `${skin}/footer/payments.png`,
  footerRg1: `${skin}/footer/rg-1.png`,
  footerRg2: `${skin}/footer/rg-2.png`,
  logo: `${skin}/brand/life-logo.png`,
  fishing: `${skin}/icons/fishing.svg`,
  liveCasino: `${skin}/icons/live-casino.png`,
  nuevos: `${skin}/icons/nuevos.png`,
  popular: `${skin}/icons/popular.png`,
  popularAlt: `${skin}/icons/popular-alt.png`,
  providerDir: `${skin}/providers`,
  providerPg: `${skin}/providers/815ac70c85846333eee8d707f35749d3.png`,
  providerPp: `${skin}/providers/733afafd8fdcf9186c8bc9d994bff68e.png`,
  providerTada: `${skin}/providers/01c233b7703242602877ab6559d00829.png`,
  slots: `${skin}/icons/slots.png`,
  sports: `${skin}/icons/sports.png`,
  pushLogo: `${skin}/brand/push-logo.png`,
  sprite: `${skin}/icons/svg-sprite.svg`,
  gameBarBank: `${skin}/game/bank.png`,
  gameBarClose: `${skin}/game/close.png`,
  gameBarCollect: `${skin}/game/collect.png`,
  gameBarSearch: `${skin}/game/search.png`,
  gameBarWallet: `${skin}/game/wallet.png`,
  gameBarWalletIos: `${skin}/game/wallet-ios.png`,
  gamePlaceholder: `${skin}/brand/life-logo.png`,
  tabEstudios: `${skin}/icons/tab-estudios.svg`,
  tabFavoritos: `${skin}/icons/tab-favoritos.svg`,
  tabLobby: `${skin}/icons/tab-lobby.svg`,
  treasureChest: `${skin}/brand/treasure-chest.png`,
};

export const oroMxBanners = [
  `${skin}/banners/1.webp`,
  `${skin}/banners/2.webp`,
  `${skin}/banners/3.webp`,
  `${skin}/banners/4.webp`,
  `${skin}/banners/5.webp`,
  `${skin}/banners/6.webp`,
];

/** 源站固定壳：Lobby / Estudios / Favoritos。分类芯片由 /nav 可见节点渲染。 */
export const oroMxChromeTabs: OroMxTab[] = [
  { id: 'lobby', label: 'Lobby', iconKind: 'img', icon: `${skin}/icons/tab-lobby.svg` },
  { id: 'estudios', label: 'Estudios', iconKind: 'img', icon: `${skin}/icons/tab-estudios.svg` },
  { id: 'favoritos', label: 'Favoritos', iconKind: 'img', icon: `${skin}/icons/tab-favoritos.svg` },
];

/** 源站 Estudios 场馆条（18）：场馆码，不是进游厂商 fakepg。 */
export const oroMxVenueCatalog = [
  { code: 'TADA', label: 'TADA', nameZh: 'TADA', file: '01c233b7703242602877ab6559d00829.png' },
  { code: 'PP', label: 'Pragmatic', nameZh: 'PP', file: '733afafd8fdcf9186c8bc9d994bff68e.png' },
  { code: 'PG', label: 'PG', nameZh: 'PG', file: '815ac70c85846333eee8d707f35749d3.png' },
  { code: 'JDB', label: 'JDB', nameZh: 'JDB', file: 'c0faafa965fffbd5773b6f61d056eef0.png' },
  { code: 'BOOMING', label: 'Booming', nameZh: 'Booming', file: '7c39bead1af233e38044b2d881835f8e.png' },
  { code: 'INOUT', label: 'InOut', nameZh: 'InOut', file: '7725c33829046f210b2e170dea117a22.png' },
  { code: 'SPINO', label: 'Spinomenal', nameZh: 'Spinomenal', file: 'b136540ae27b32ec1831060075725642.png' },
  { code: 'EVO', label: 'Evolution', nameZh: 'Evolution', file: '242900ec300a0789652d31bc367c6b0b.png' },
  { code: 'RT', label: 'Red Tiger', nameZh: 'Red Tiger', file: '652e310d94a9d8e41d9cd122cdda7ae3.png' },
  { code: 'PGS', label: 'PG Soft', nameZh: 'PG Soft', file: '4019bdb16b25246d01cfbe4ec4200eaf.svg' },
  { code: 'FC', label: 'Fa Chai', nameZh: 'FC', file: '98c25d0789235d7a94da206cee5f6784.webp' },
  { code: 'MG', label: 'Microgaming', nameZh: 'MG', file: '73bd95732b5f0947c8d64bf0e9eaf42c.png' },
  { code: 'BGM', label: 'BGaming', nameZh: 'BGaming', file: '2308993aa1a8352460afb3f6d571eb21.png' },
  { code: 'SPDG', label: 'Spadegaming', nameZh: 'Spade', file: '538487407b04366786cf9fecfd457018.png' },
  { code: 'HSD', label: 'Hacksaw', nameZh: 'Hacksaw', file: '213c2c37217b3463879972388b32d7e3.png' },
  { code: 'CQ9', label: 'CQ9', nameZh: 'CQ9', file: '3a45211d0753c176e42c83db3633d041.png' },
  { code: 'EZUGI', label: 'Ezugi', nameZh: 'Ezugi', file: 'ea97d12e43abbb7927f9c21117ab498c.png' },
  { code: 'EVOP', label: 'Evoplay', nameZh: 'Evoplay', file: '0e1c323d270086a11e85575589e3a681.png' },
] as const;

export const oroMxProviders = oroMxVenueCatalog.map((venue) => `${skin}/providers/${venue.file}`);

export const oroMxPopularGames: OroMxGame[] = [
  { id: 'PP_vs20olympgold', title: 'Gates of Olympus Super Scatter', image: `${skin}/games/PP_vs20olympgold.png` },
  { id: 'PP_vs40cosmiccash', title: 'Cosmic Cash', image: `${skin}/games/PP_vs40cosmiccash.png` },
  { id: 'PP_vs25goldparty', title: 'Gold Party', image: `${skin}/games/PP_vs25goldparty.png` },
  { id: 'PP_vs5aztecgems', title: 'Aztec Gems', image: `${skin}/games/PP_vs5aztecgems.png` },
  { id: 'PP_vs20sugarrush', title: 'Sugar Rush', image: `${skin}/games/PP_vs20sugarrush.png` },
  { id: 'PP_vs5joker', title: "Joker's Jewels", image: `${skin}/games/PP_vs5joker.png` },
  { id: 'PP_vs20olympgcl', title: 'Fortune of Olympus', image: `${skin}/games/PP_vs20olympgcl.png` },
  { id: 'PP_vs20swrbon', title: 'Sweet Rush Bonanza', image: `${skin}/games/PP_vs20swrbon.png` },
  { id: 'PP_vs20sugrushss', title: 'Sugar Rush Super Scatter', image: `${skin}/games/PP_vs20sugrushss.png` },
  { id: 'PP_vs20fruitswx', title: 'Sweet Bonanza 1000', image: `${skin}/games/PP_vs20fruitswx.png` },
  { id: 'PP_vswayslions', title: '5 Lions Megaways', image: `${skin}/games/PP_vswayslions.png` },
  { id: 'PP_vs25chilli', title: 'Chilli Heat', image: `${skin}/games/PP_vs25chilli.png` },
  { id: 'PP_vs5jokerjc', title: "Joker’s Jewels Cash", image: `${skin}/games/PP_vs5jokerjc.png` },
  { id: 'INOUT_plinko', title: 'Plinko 1000', image: `${skin}/games/INOUT_plinko.png` },
  { id: 'INOUT_mines', title: 'Mines', image: `${skin}/games/INOUT_mines.png` },
  { id: 'GPKBM_troll', title: 'Trollfufu Bonanza', image: `${skin}/games/GPKBM_troll.png` },
  { id: 'GPKBM_pig', title: 'Cash Pig 2 15,000', image: `${skin}/games/GPKBM_pig.png` },
  { id: 'GPKBM_burn', title: 'Burning Classics 2', image: `${skin}/games/GPKBM_burn.png` },
  { id: 'GPKSPINO_king', title: 'Majestic King', image: `${skin}/games/GPKSPINO_king.png` },
  { id: 'GPKSPINO_777', title: 'Retro 777 Space', image: `${skin}/games/GPKSPINO_777.png` },
  { id: 'GPKBG_tiger', title: 'Wild Tiger', image: `${skin}/games/GPKBG_tiger.png` },
  { id: 'GPKBG_lucky8', title: 'Lucky 8 Merge Up', image: `${skin}/games/GPKBG_lucky8.png` },
  { id: 'HSD_1482', title: 'Slayers Inc', image: `${skin}/games/HSD_1482.png` },
  { id: 'HSD_1309', title: 'Le Bandit', image: `${skin}/games/HSD_1309.png` },
  { id: 'EVOP_6458', title: 'EVOP 6458', image: `${skin}/games/EVOP_6458.png` },
  { id: 'EVOP_6331', title: 'EVOP 6331', image: `${skin}/games/EVOP_6331.png` },
  { id: 'MG_SMG_novaBlastUltra', title: 'Nova Blast Ultra', image: `${skin}/games/MG_SMG_novaBlastUltra.png` },
  { id: 'MG_SMG_luckyRainbowRush', title: 'Lucky Rainbow Rush', image: `${skin}/games/MG_SMG_luckyRainbowRush.png` },
  { id: 'MG_SMG_cluckinSweetFeast', title: "Cluckin' Sweet Feast", image: `${skin}/games/MG_SMG_cluckinSweetFeast.png` },
  { id: 'JDB_0_14102', title: 'JDB 14102', image: `${skin}/games/JDB_0_14102.png` },
  { id: 'JDB_0_14085', title: 'JDB 14085', image: `${skin}/games/JDB_0_14085.png` },
  { id: 'JDB_0_14045', title: 'JDB 14045', image: `${skin}/games/JDB_0_14045.png` },
  { id: 'SPDG_S-MG01', title: 'SPDG MG01', image: `${skin}/games/SPDG_S-MG01.png` },
  { id: 'SPDG_S-FS01', title: 'SPDG FS01', image: `${skin}/games/SPDG_S-FS01.png` },
  { id: 'CQ9_133', title: 'CQ9 133', image: `${skin}/games/CQ9_133.png` },
  { id: 'PGS_89', title: 'PGS 89', image: `${skin}/games/PGS_89.png` },
  { id: 'IMG_5beca7cf', title: 'Popular', image: `${skin}/games/IMG_5beca7cf.jpg` },
  { id: 'PGS_126', title: 'PGS 126', image: `${skin}/games/PGS_126.png` },
  { id: 'FC_22092', title: 'FC 22092', image: `${skin}/games/FC_22092.png` },
  { id: 'FC_22066', title: 'FC 22066', image: `${skin}/games/FC_22066.png` },
  { id: 'FC_22043', title: 'FC 22043', image: `${skin}/games/FC_22043.png` },
];

export const oroMxPopularAltGames: OroMxGame[] = [
  { id: 'PP_vs20sugarrushx', title: 'PP_vs20sugarrushx', image: `${skin}/games/PP_vs20sugarrushx.png` },
  { id: 'PP_vs20olympgate', title: 'PP_vs20olympgate', image: `${skin}/games/PP_vs20olympgate.png` },
  { id: 'PP_vs20olympx', title: 'PP_vs20olympx', image: `${skin}/games/PP_vs20olympx.png` },
  { id: 'PP_vswaysbufking', title: 'PP_vswaysbufking', image: `${skin}/games/PP_vswaysbufking.png` },
  { id: 'PP_vs20fruitsw', title: 'PP_vs20fruitsw', image: `${skin}/games/PP_vs20fruitsw.png` },
  { id: 'PP_vs10txbigbass', title: 'PP_vs10txbigbass', image: `${skin}/games/PP_vs10txbigbass.png` },
  { id: 'INOUT_penalty-unlimited', title: 'INOUT_penalty-unlimited', image: `${skin}/games/INOUT_penalty-unlimited.png` },
  { id: 'INOUT_chicken-road-two', title: 'INOUT_chicken-road-two', image: `${skin}/games/INOUT_chicken-road-two.png` },
  { id: 'GPKBM_690db8e2c13324b79c4ea61e', title: 'GPKBM_690db8e2c13324b79c4ea61e', image: `${skin}/games/GPKBM_690db8e2c13324b79c4ea61e.png` },
  { id: 'GPKBM_6818bdf22a8d4da85b5622e3', title: 'GPKBM_6818bdf22a8d4da85b5622e3', image: `${skin}/games/GPKBM_6818bdf22a8d4da85b5622e3.png` },
  { id: 'GPKBM_669e56d3e752ad00018998c8', title: 'GPKBM_669e56d3e752ad00018998c8', image: `${skin}/games/GPKBM_669e56d3e752ad00018998c8.png` },
  { id: 'GPKSPINO_SlotMachine_StoryOfZeus', title: 'GPKSPINO_SlotMachine_StoryOfZeus', image: `${skin}/games/GPKSPINO_SlotMachine_StoryOfZeus.png` },
  { id: 'GPKSPINO_SlotMachine_StoryOfMedusaII-TGE', title: 'GPKSPINO_SlotMachine_StoryOfMedusaII-TGE', image: `${skin}/games/GPKSPINO_SlotMachine_StoryOfMedusaII-TGE.png` },
  { id: 'GPKSPINO_SlotMachine_FortunaDeLosMuertos4', title: 'GPKSPINO_SlotMachine_FortunaDeLosMuertos4', image: `${skin}/games/GPKSPINO_SlotMachine_FortunaDeLosMuertos4.png` },
  { id: 'GPKSPINO_SlotMachine_4Horsemen3', title: 'GPKSPINO_SlotMachine_4Horsemen3', image: `${skin}/games/GPKSPINO_SlotMachine_4Horsemen3.png` },
  { id: 'PP_205', title: 'PP_205', image: `${skin}/games/PP_205.png` },
  { id: 'GPKBG_WildCashX9990', title: 'GPKBG_WildCashX9990', image: `${skin}/games/GPKBG_WildCashX9990.png` },
  { id: 'GPKBG_PandaLuck', title: 'GPKBG_PandaLuck', image: `${skin}/games/GPKBG_PandaLuck.png` },
  { id: 'GPKBG_JokerXLove', title: 'GPKBG_JokerXLove', image: `${skin}/games/GPKBG_JokerXLove.png` },
  { id: 'GPKBG_GoldRushWithJohnny', title: 'GPKBG_GoldRushWithJohnny', image: `${skin}/games/GPKBG_GoldRushWithJohnny.png` },
  { id: 'GPKBG_BurningChilliX', title: 'GPKBG_BurningChilliX', image: `${skin}/games/GPKBG_BurningChilliX.png` },
  { id: 'EZUGI_1', title: 'EZUGI_1', image: `${skin}/games/EZUGI_1.png` },
  { id: 'EVOP_5752', title: 'EVOP_5752', image: `${skin}/games/EVOP_5752.png` },
  { id: 'MG_SMG_tigersTreasures', title: 'MG_SMG_tigersTreasures', image: `${skin}/games/MG_SMG_tigersTreasures.png` },
  { id: 'MG_SMG_sweetJarXmasCombo', title: 'MG_SMG_sweetJarXmasCombo', image: `${skin}/games/MG_SMG_sweetJarXmasCombo.png` },
  { id: 'MG_SMG_jokerLokosMultiplierTrail', title: 'MG_SMG_jokerLokosMultiplierTrail', image: `${skin}/games/MG_SMG_jokerLokosMultiplierTrail.png` },
  { id: 'MG_SMG_3BlazingVolcanoesPowerCombo', title: 'MG_SMG_3BlazingVolcanoesPowerCombo', image: `${skin}/games/MG_SMG_3BlazingVolcanoesPowerCombo.png` },
  { id: 'MG_SMG_12SkullsOfTheDead', title: 'MG_SMG_12SkullsOfTheDead', image: `${skin}/games/MG_SMG_12SkullsOfTheDead.png` },
  { id: 'JDB_0_14098', title: 'JDB_0_14098', image: `${skin}/games/JDB_0_14098.png` },
  { id: 'JDB_0_14092', title: 'JDB_0_14092', image: `${skin}/games/JDB_0_14092.png` },
  { id: 'JDB_0_14036', title: 'JDB_0_14036', image: `${skin}/games/JDB_0_14036.png` },
  { id: 'CQ9_179', title: 'CQ9_179', image: `${skin}/games/CQ9_179.png` },
  { id: 'CQ9_105', title: 'CQ9_105', image: `${skin}/games/CQ9_105.png` },
  { id: 'PGS_68', title: 'PGS_68', image: `${skin}/games/PGS_68.png` },
  { id: 'PGS_1543462', title: 'PGS_1543462', image: `${skin}/games/PGS_1543462.png` },
  { id: 'PGS_1492288', title: 'PGS_1492288', image: `${skin}/games/PGS_1492288.png` },
  { id: 'FC_27001', title: 'FC_27001', image: `${skin}/games/FC_27001.png` },
  { id: 'FC_22068', title: 'FC_22068', image: `${skin}/games/FC_22068.png` },
  { id: 'FC_22060', title: 'FC_22060', image: `${skin}/games/FC_22060.png` },
  { id: 'FC_22042', title: 'FC_22042', image: `${skin}/games/FC_22042.png` },
  { id: 'FC_22037', title: 'FC_22037', image: `${skin}/games/FC_22037.png` },
];

export const oroMxNuevosGames: OroMxGame[] = [
  { id: 'FC_22086', title: 'FC_22086', image: `${skin}/games/FC_22086.png` },
  { id: 'FC_22091', title: 'FC_22091', image: `${skin}/games/FC_22091.png` },
  { id: 'INOUT_joker-pyre', title: 'INOUT_joker-pyre', image: `${skin}/games/INOUT_joker-pyre.png` },
  { id: 'INOUT_chicken-road', title: 'INOUT_chicken-road', image: `${skin}/games/INOUT_chicken-road.png` },
  { id: 'GPKBM_683d70508bcef6b5fd932625', title: 'GPKBM_683d70508bcef6b5fd932625', image: `${skin}/games/GPKBM_683d70508bcef6b5fd932625.png` },
  { id: 'GPKBM_67a22baf2b5971443f2b31ea', title: 'GPKBM_67a22baf2b5971443f2b31ea', image: `${skin}/games/GPKBM_67a22baf2b5971443f2b31ea.png` },
  { id: 'GPKBM_678a2f0c80afaeaeee5b0387', title: 'GPKBM_678a2f0c80afaeaeee5b0387', image: `${skin}/games/GPKBM_678a2f0c80afaeaeee5b0387.png` },
  { id: 'GPKSPINO_Tower_1ReelDemiGodsIII-CW', title: 'GPKSPINO_Tower_1ReelDemiGodsIII-CW', image: `${skin}/games/GPKSPINO_Tower_1ReelDemiGodsIII-CW.png` },
  { id: 'GPKSPINO_SlotMachine_MajesticKing-Sunset', title: 'GPKSPINO_SlotMachine_MajesticKing-Sunset', image: `${skin}/games/GPKSPINO_SlotMachine_MajesticKing-Sunset.png` },
  { id: 'GPKSPINO_SlotMachine_BookOfSirens-GoldenPearl', title: 'GPKSPINO_SlotMachine_BookOfSirens-GoldenPearl', image: `${skin}/games/GPKSPINO_SlotMachine_BookOfSirens-GoldenPearl.png` },
  { id: 'GPKSPINO_SlotMachine_BookOfRampage', title: 'GPKSPINO_SlotMachine_BookOfRampage', image: `${skin}/games/GPKSPINO_SlotMachine_BookOfRampage.png` },
  { id: 'GPKBG_WildCash', title: 'GPKBG_WildCash', image: `${skin}/games/GPKBG_WildCash.png` },
  { id: 'GPKBG_BonanzaTrillion', title: 'GPKBG_BonanzaTrillion', image: `${skin}/games/GPKBG_BonanzaTrillion.png` },
  { id: 'HSD_2057', title: 'HSD_2057', image: `${skin}/games/HSD_2057.png` },
  { id: 'HSD_1067', title: 'HSD_1067', image: `${skin}/games/HSD_1067.png` },
  { id: 'EVOP_6386', title: 'EVOP_6386', image: `${skin}/games/EVOP_6386.png` },
  { id: 'EVOP_5517', title: 'EVOP_5517', image: `${skin}/games/EVOP_5517.png` },
  { id: 'MG_SMG_mayanBoost', title: 'MG_SMG_mayanBoost', image: `${skin}/games/MG_SMG_mayanBoost.png` },
  { id: 'MG_SMG_goldBlitzExtreme', title: 'MG_SMG_goldBlitzExtreme', image: `${skin}/games/MG_SMG_goldBlitzExtreme.png` },
  { id: 'JDB_0_14100', title: 'JDB_0_14100', image: `${skin}/games/JDB_0_14100.png` },
  { id: 'JDB_0_14093', title: 'JDB_0_14093', image: `${skin}/games/JDB_0_14093.png` },
  { id: 'JDB_0_14091', title: 'JDB_0_14091', image: `${skin}/games/JDB_0_14091.png` },
  { id: 'SPDG_S-FS02', title: 'SPDG_S-FS02', image: `${skin}/games/SPDG_S-FS02.png` },
  { id: 'SPDG_S-FH03', title: 'SPDG_S-FH03', image: `${skin}/games/SPDG_S-FH03.png` },
  { id: 'CQ9_7', title: 'CQ9_7', image: `${skin}/games/CQ9_7.png` },
  { id: 'CQ9_52', title: 'CQ9_52', image: `${skin}/games/CQ9_52.png` },
  { id: 'PGS_87', title: 'PGS_87', image: `${skin}/games/PGS_87.png` },
  { id: 'PGS_1695365', title: 'PGS_1695365', image: `${skin}/games/PGS_1695365.png` },
  { id: 'PGS_104', title: 'PGS_104', image: `${skin}/games/PGS_104.png` },
  { id: 'FC_22095', title: 'FC_22095', image: `${skin}/games/FC_22095.png` },
  { id: 'FC_22075', title: 'FC_22075', image: `${skin}/games/FC_22075.png` },
  { id: 'FC_22072', title: 'FC_22072', image: `${skin}/games/FC_22072.png` },
  { id: 'FC_22071', title: 'FC_22071', image: `${skin}/games/FC_22071.png` },
  { id: 'FC_22067', title: 'FC_22067', image: `${skin}/games/FC_22067.png` },
  { id: 'FC_22062', title: 'FC_22062', image: `${skin}/games/FC_22062.png` },
  { id: 'FC_22058', title: 'FC_22058', image: `${skin}/games/FC_22058.png` },
  { id: 'FC_22053', title: 'FC_22053', image: `${skin}/games/FC_22053.png` },
  { id: 'FC_22050', title: 'FC_22050', image: `${skin}/games/FC_22050.png` },
  { id: 'FC_22047', title: 'FC_22047', image: `${skin}/games/FC_22047.png` },
  { id: 'FC_22045', title: 'FC_22045', image: `${skin}/games/FC_22045.png` },
  { id: 'FC_22026', title: 'FC_22026', image: `${skin}/games/FC_22026.png` },
  { id: 'FC_22024', title: 'FC_22024', image: `${skin}/games/FC_22024.png` },
  { id: 'FC_22023', title: 'FC_22023', image: `${skin}/games/FC_22023.png` },
];

export function oroMxGamePages(games: OroMxGame[], size = 9) {
  const pages: OroMxGame[][] = [];
  for (let i = 0; i < games.length; i += size) pages.push(games.slice(i, i + size));
  return pages;
}

export type OroMxLiveWin = {
  amount: string;
  game: string;
  image: string;
};

export const oroMxLiveWins: OroMxLiveWin[] = [
  { amount: '$5,168.00', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$11,177.10', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$5,664.80', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$2,388.48', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$1,746.55', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$5,373.70', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$1,752.68', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$1,702.80', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$4,773.60', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$2,108.01', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$3,364.90', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$9,678.57', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$17,548.80', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$1,259.90', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
  { amount: '$8,475.60', game: 'Ice Fishing', image: `${skin}/live-win/ice-fishing.webp` },
  { amount: '$5,098.94', game: 'Crazy Time', image: `${skin}/live-win/crazy-time.webp` },
];

export function findOroMxGame(id: string): OroMxGame | undefined {
  const pools = [oroMxPopularGames, oroMxPopularAltGames, oroMxNuevosGames, oroMxSearchRecommended];
  for (const pool of pools) {
    const hit = pool.find((game) => game.id === id);
    if (hit) return hit;
  }
  return undefined;
}

export const oroMxSearchRecommended: OroMxGame[] = [
  { id: 'TD_540', title: 'TD 540', image: `${skin}/search/TD_540.png` },
  { id: 'TD_664', title: 'TD 664', image: `${skin}/search/TD_664.png` },
  { id: 'TD_223A', title: 'TD 223A', image: `${skin}/search/TD_223A.png` },
  { id: 'TD_109', title: 'TD 109', image: `${skin}/search/TD_109.png` },
  { id: 'PP_vs20sugarrushx', title: 'Sugar Rush X', image: `${skin}/search/PP_vs20sugarrushx.png` },
  { id: 'PP_vs20olympgate', title: 'Gates of Olympus', image: `${skin}/search/PP_vs20olympgate.png` },
];

export type OroMxFloatSheet = {
  /** 每行帧数。 */
  cols: number;
  /** 总帧数。 */
  count: number;
  /** 单帧边长 px（与 float 按钮 canvas 144 一致）。 */
  frame: number;
  src: string;
};

/** 浮动按钮序列帧已合成为单张 WebP 雪碧图（9 列，144px/帧，按行铺）。 */
export function oroMxFloatSheet(
  folder: 'jackpot-window' | 'scratch' | 'sign-in' | 'wheel',
  count: number,
): OroMxFloatSheet {
  return { cols: 9, count, frame: 144, src: `${skin}/float/${folder}-sheet.webp` };
}

/** 雪碧图里第 index 帧的左上角坐标。 */
export function oroMxFloatSheetOffset(sheet: OroMxFloatSheet, index: number) {
  const i = ((index % sheet.count) + sheet.count) % sheet.count;
  return { x: (i % sheet.cols) * sheet.frame, y: Math.floor(i / sheet.cols) * sheet.frame };
}
